import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import type { Perms, Role } from '@farmos/shared';
import { api } from './api';
import { applyLocale, type Locale } from './i18n';
import { supabase } from './supabase';

export interface Me {
  id: string;
  name: string;
  role: Role;
  locale: Locale;
  perms: Perms;
  inbox_id: string;
  /** FarmAI is switched on for the farm */
  enabled: boolean;
}

interface AuthState {
  session: Session | null;
  me: Me | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) qc.clear();
    });
    return () => data.subscription.unsubscribe();
  }, [qc]);

  const uid = session?.user.id;
  const me = useQuery({
    enabled: !!uid,
    queryKey: ['me', uid],
    queryFn: () => api<Me>('/me'),
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (me.data) applyLocale(me.data.locale);
  }, [me.data]);

  return (
    <AuthContext.Provider
      value={{
        session,
        me: me.data ?? null,
        loading: !ready || (!!uid && me.isPending),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
